<template>
  <loading-view
    v-if="showLoadingOverlay"
    :cancellable="notCancellableLoadingProcesses.length === 0"
    :circular-progress="circularProgress"
    @cancel="cancel"
  />
  <status-bar />

  <legal-terms-dialog v-if="showTerms" @terms-accepted="interaction.raise('termsAccepted')" />

  <ending-subscription-dialog
    v-if="showEndingSubscription"
    :days-left="remainingDays"
    @continue="showEndingSubscription = false"
  />
  <subscription-ended-dialoge v-if="showSubscriptionEnded" @continue="showSubscriptionEnded = false" />
  <statechart-editor v-if="showEditor" />
  <onboarding-view />
</template>

<script setup lang="ts">
import LegalTermsDialog from "../TermsAndConditions/LegalTermsDialog.vue";
import StatechartEditor from "../Editor/StatechartEditor.vue";
import OnboardingView from "../Onboarding/OnboardingView.vue";
import StatusBar from "../StatusBar/StatusBar.vue";
import LoadingView from "../Editor/Loading/LoadingView.vue";
import { tutorial as guide01 } from "../Onboarding/steps/01-ui-guide";
import { onBeforeUnmount, onMounted, onUnmounted, ref, watchEffect } from "vue";
import { KeyEventBus, MessageEventBus, TourEventBus } from "../event-bus";
import { useStore } from "vuex";
import { useUserStore } from "../Authentication/store/userStore";
import { useBillingStore } from "../../stores/billingStore";
import { ProgressBarEventBus } from "../event-bus";
import EndingSubscriptionDialog from "../TermsAndConditions/EndingSubscriptionDialog.vue";
import SubscriptionEndedDialoge from "@/components/TermsAndConditions/SubscriptionEndedDialoge.vue";
import { State, StateMachine } from "../../utils/state-machine";
import { PreferenceStore } from "../Editor/services/preference-store";

const userStore = useUserStore();
const billingStore = useBillingStore();
const authStore = useStore();

const showEditor = ref<boolean>(false);
const showTerms = ref<boolean>(false);
const showEndingSubscription = ref<boolean>(false);
const showSubscriptionEnded = ref<boolean>(false);
const remainingDays = ref<number>(0);

const showLoadingOverlay = ref<boolean>(false);
const notCancellableLoadingProcesses = ref<string[]>([]);
const circularProgress = ref<number>(0);

onMounted(() => {
  window.addEventListener("keyup", keyEventHandler);

  ProgressBarEventBus.on("startTask", activateLoadingView);
  ProgressBarEventBus.on("finishedTask", finishLoading);

  interaction.enter();
});

onBeforeUnmount(() => {
  ProgressBarEventBus.off("startTask", activateLoadingView);
  ProgressBarEventBus.off("finishedTask", finishLoading);
  window.removeEventListener("keyup", keyEventHandler);
});

onUnmounted(() => {
  ProgressBarEventBus.all.clear();
  KeyEventBus.all.clear();
});

watchEffect(async () => {
  const token = authStore.getters.oidcAccessToken;
  if (token) {
    await userStore.updateUserInfo();
    remainingDays.value = userStore.getUserInfo.remainingDays;
    interaction.react();
  }
});

function activateLoadingView(evt = { progress: 0, notCancellableId: undefined }) {
  if (evt.notCancellableId) {
    notCancellableLoadingProcesses.value.push(evt.notCancellableId);
  }
  circularProgress.value = evt.progress;
  showLoadingOverlay.value = true;
}

function cancel() {
  if (notCancellableLoadingProcesses.value.length === 0) {
    circularProgress.value = 0;
    showLoadingOverlay.value = false;
  }
}

function finishLoading(id = undefined) {
  if (id) {
    notCancellableLoadingProcesses.value = notCancellableLoadingProcesses.value.filter((el) => {
      return el !== id;
    });
  }
  cancel();
}

function keyEventHandler(event: KeyboardEvent) {
  switch (event.key) {
    case "Enter":
      KeyEventBus.emit("enter");
      break;
    case "Escape":
      KeyEventBus.emit("esc");
  }
}

const INITIALIZE = new State()
  .onEnter(() => {
    ProgressBarEventBus.emit("startTask", {
      progress: 0,
      notCancellableId: "initial-loading",
    });
    showEditor.value = true;
  })
  .onReact((_) => {
    const user = userStore.getUserInfo;
    if (user.status !== undefined) {
      if (user.status === "ACTION_REQUIRED") {
        _.activate(ACTION_REQUIRED);
      } else {
        _.activate(CHECK_SUBSCRIPTION);
      }
    }
  });

const ACTION_REQUIRED = new State()
  .onEnter(() => {
    if (userStore.getUserInfo.requiredAction === "ACCEPT_LEGAL_TERMS") showTerms.value = true;
  })
  .onReact((_) => {
    if (_.on("termsAccepted")) _.activate(CHECK_SUBSCRIPTION);
  })
  .onExit(() => {
    TourEventBus.emit("startOnboarding", guide01.steps);
    showTerms.value = false;
  });

const CHECK_SUBSCRIPTION = new State().onReact(async (_) => {
  await userStore.updateUserInfo();
  remainingDays.value = userStore.getUserInfo.remainingDays;
  if (remainingDays.value <= 0) {
    showSubscriptionEnded.value = true;
  } else if (remainingDays.value <= 7 && remainingDays.value > 0) {
    showEndingSubscription.value = true;
  } else if (remainingDays.value <= 30) {
    setTimeout(() => {
      MessageEventBus.emit("information", {
        text: `${remainingDays.value} days are remaining from your current subscription`,
        id: "statechart-editor",
        showProgress: false,
        timeout: 10000,
      });
    }, 10000);
  }
  _.activate(EDITING);
});

const EDITING = new State()
  .onEnter(async () => {
    try {
      await billingStore.updateBillingStatus();
    } catch {
      // billing not available — billingExists stays false
    }
  })
  .onReact((_) => {
    PreferenceStore.INSTANCE.isReadOnly = remainingDays.value <= 0;
  });

const interaction = new StateMachine(INITIALIZE);
</script>
